import { Injectable, isDevMode, signal } from '@angular/core';

import {
  CADS_TOAST_DEDUPE_WINDOW_MS,
  CADS_TOAST_DEFAULT_DURATION_MS,
  CADS_TOAST_EXIT_DURATION_MS,
  type CadsToastConfig,
  type CadsToastInstance,
} from './cads-toast.config';

type PartialToastConfig = Omit<CadsToastConfig, 'message'>;

/**
 * App-wide CADS toast orchestrator: a stack of transient toasts rendered by
 * {@link CadsGlobalToastHostComponent}. Independent of legacy `ToastService`.
 */
@Injectable({ providedIn: 'root' })
export class CadsGlobalToastService {
  private readonly _toasts = signal<readonly CadsToastInstance[]>([]);
  private readonly _autoDismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly _exitTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly _dedupeTimestamps = new Map<string, number>();
  private _hostRegistered = false;

  /** Active toast stack; consumed by {@link CadsGlobalToastHostComponent}. */
  readonly toasts = this._toasts.asReadonly();

  /** Called by {@link CadsGlobalToastHostComponent} on init so {@link show} can warn when no host is mounted. */
  registerHost(): void {
    this._hostRegistered = true;
  }

  /**
   * Shows a transient toast and returns its id. Duplicate {@link CadsToastConfig.dedupeKey} shows within
   * {@link CADS_TOAST_DEDUPE_WINDOW_MS} are ignored (returns empty string).
   */
  show(config: CadsToastConfig): string {
    if (!this._hostRegistered && isDevMode()) {
      console.warn(
        'CadsGlobalToastService.show() called before a CadsGlobalToastHostComponent was mounted. ' +
          'Add <cads-global-toast-host /> to the app shell.',
      );
    }

    const message = config.message.trim();
    if (message === '') {
      return '';
    }

    if (config.dedupeKey) {
      const lastShown = this._dedupeTimestamps.get(config.dedupeKey);
      const now = Date.now();
      if (lastShown != null && now - lastShown < CADS_TOAST_DEDUPE_WINDOW_MS) {
        return '';
      }
      this._dedupeTimestamps.set(config.dedupeKey, now);
    }

    const id = crypto.randomUUID();
    const variant = config.variant ?? 'info';
    const instance: CadsToastInstance = { id, message, variant, exiting: false };
    this._toasts.update((current) => [...current, instance]);

    const durationMs = config.durationMs ?? CADS_TOAST_DEFAULT_DURATION_MS;
    const timer = setTimeout(() => {
      this._autoDismissTimers.delete(id);
      this.dismiss(id);
    }, durationMs);
    this._autoDismissTimers.set(id, timer);

    return id;
  }

  /** Shows a success toast; returns toast id (or empty string when deduped). */
  success(message: string, options?: PartialToastConfig): string {
    return this.show({ ...options, message, variant: 'success' });
  }

  /** Shows an error toast; returns toast id (or empty string when deduped). */
  error(message: string, options?: PartialToastConfig): string {
    return this.show({ ...options, message, variant: 'error' });
  }

  /** Shows an info toast; returns toast id (or empty string when deduped). */
  info(message: string, options?: PartialToastConfig): string {
    return this.show({ ...options, message, variant: 'info' });
  }

  /** Begins exit animation for one toast, then removes it from the stack. */
  dismiss(id: string): void {
    const autoTimer = this._autoDismissTimers.get(id);
    if (autoTimer != null) {
      clearTimeout(autoTimer);
      this._autoDismissTimers.delete(id);
    }

    if (this._exitTimers.has(id)) {
      return;
    }

    const exists = this._toasts().some((toast) => toast.id === id);
    if (!exists) {
      return;
    }

    this._toasts.update((current) =>
      current.map((toast) => (toast.id === id ? { ...toast, exiting: true } : toast)),
    );

    const exitTimer = setTimeout(() => {
      this._exitTimers.delete(id);
      this._toasts.update((current) => current.filter((toast) => toast.id !== id));
    }, CADS_TOAST_EXIT_DURATION_MS);
    this._exitTimers.set(id, exitTimer);
  }

  /** Dismisses every active toast. */
  dismissAll(): void {
    for (const toast of this._toasts()) {
      this.dismiss(toast.id);
    }
  }
}

/** @internal Test helper to reset service state between specs. */
export function resetCadsGlobalToastServiceForTesting(service: CadsGlobalToastService): void {
  const internal = service as unknown as {
    _toasts: { set(value: readonly CadsToastInstance[]): void };
    _autoDismissTimers: Map<string, ReturnType<typeof setTimeout>>;
    _exitTimers: Map<string, ReturnType<typeof setTimeout>>;
    _dedupeTimestamps: Map<string, number>;
    _hostRegistered: boolean;
  };
  for (const timer of internal._autoDismissTimers.values()) {
    clearTimeout(timer);
  }
  for (const timer of internal._exitTimers.values()) {
    clearTimeout(timer);
  }
  internal._autoDismissTimers.clear();
  internal._exitTimers.clear();
  internal._dedupeTimestamps.clear();
  internal._toasts.set([]);
  internal._hostRegistered = false;
}
